import { useQuery, useQueryClient } from 'react-query'
import { checkTableExists, getDataExplorerObjectQuery, getSubscriptionDatesQuery } from '../api/dataExplorerQueries'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getDataTreeResource } from '../api/dataExplorer'
import { JobStatusType, ProcessingJob } from 'features/jobs/types'
import useProcessingJobs from 'features/jobs/ui/useJobs/useProcessingJobs'
import { queryClient } from 'queryClient'
import { useAppSelector } from 'app/state'

function useDataExplorerTable(fqdn: string, connectionId: string) {
  const { data: table } = useQuery(getDataExplorerObjectQuery(fqdn, connectionId))
  return table
}

export function useSubscriptionDatesQuery(fqn: string) {
  const { data: dates, isLoading } = useQuery(getSubscriptionDatesQuery(fqn))
  return { data: dates?.rows, isLoading }
}

export function useOptimizedTableJob({ fqn, connection }) {
  const { processingJobs } = useProcessingJobs()
  const [optimizedTableJob, setOptimizedTableJob] = useState<ProcessingJob | undefined>()

  useEffect(() => {
    const doCheck = async () => {
      const processingJob = processingJobs.find((job) => {
        return job.response.metadata.optimizedTableId === fqn && job.status?.status === JobStatusType.Success
      })
      const optimizedTableExists =
        processingJob &&
        (await queryClient.fetchQuery(checkTableExists({ connection, fqn: processingJob.response.metadata.name })))
      if (!optimizedTableExists) {
        setOptimizedTableJob(undefined)
      } else {
        setOptimizedTableJob(processingJob)
      }
    }

    doCheck()
  }, [connection, fqn, processingJobs])

  return optimizedTableJob
}

export function useDataExplorerLocationPath() {
  const currentSelectedTreeItemId = useAppSelector((state) => state.dataExplorer.currentSelectedTreeItemId)
  return currentSelectedTreeItemId
}

export function useDataTreeResults(keyword, { connectionId, projectId, datasetId }) {
  const queryKey = useMemo(
    () => ['treeResults', connectionId, projectId, datasetId, keyword],
    [connectionId, projectId, datasetId, keyword]
  )
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      if (!connectionId) {
        return
      }

      const dataset = datasetId && `${projectId}.${datasetId}`
      return getDataTreeResource(
        {
          connectionId,
          projectId,
          datasetId: dataset,
          search: keyword
        },
        { signal }
      )
    }
  })

  const cancelQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryKey, queryClient])

  return { data, isLoading, cancelQuery }
}

export default useDataExplorerTable
